import React from "react";
import Buttons from "../../components/Button";
import {
  Product,
  AttributeInput,
  VariantInput,
  FieldItem,
} from "../../interfaces/adminproduct";

interface ProductFormProps {
  formData: Product;
  setFormData: React.Dispatch<React.SetStateAction<Product>>;
  isEditing: boolean;
  handleSubmit: (e?: React.FormEvent) => void;
  resetForm: () => void;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  imagePreviews: string[];
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleVideoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  categories: { id: string; name: string; parentCategoryId?: string }[];
  shops: any[];
  vendors: any[];
  fieldTabs: FieldItem[][];
}

const ProductForm: React.FC<ProductFormProps> = ({
  formData,
  setFormData,
  isEditing,
  handleSubmit,
  resetForm,
  setIsEditing,
  imagePreviews,
  handleImageChange,
  handleVideoChange,
  categories,
  vendors,
  shops,
  fieldTabs,
}) => {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? "" : parseFloat(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const [filteredShops, setFilteredShops] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (formData.vendorId) {
      const filtered = shops.filter(
        (shop) => shop.vendorId === formData.vendorId
      );
      setFilteredShops(filtered);
    } else {
      setFilteredShops([]);
    }
  }, [formData.vendorId, shops]);

  // Attribute Handlers
  const handleAttributeChange = (
    index: number,
    field: keyof AttributeInput,
    value: string
  ) => {
    setFormData((prev) => {
      const updatedAttributes = [...(prev.attributes || [])];
      updatedAttributes[index] = {
        ...updatedAttributes[index],
        [field]: value,
      };
      return { ...prev, attributes: updatedAttributes };
    });
  };

  const addAttribute = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [...(prev.attributes || []), { key: "", value: "" }],
    }));
  };

  const removeAttribute = (index: number) => {
    setFormData((prev) => {
      const updatedAttributes = [...(prev.attributes || [])];
      updatedAttributes.splice(index, 1);
      return { ...prev, attributes: updatedAttributes };
    });
  };

  // Variant Handlers
  const handleVariantChange = (
    index: number,
    field: keyof VariantInput,
    value: any
  ) => {
    setFormData((prev) => {
      const updatedVariants = [...(prev.variants || [])];
      updatedVariants[index] = {
        ...updatedVariants[index],
        [field]: value,
      };
      return { ...prev, variants: updatedVariants };
    });
  };

  const handleVariantPropertyChange = (
    variantIndex: number,
    propIndex: number,
    field: keyof AttributeInput,
    value: string
  ) => {
    setFormData((prev) => {
      const updatedVariants = [...(prev.variants || [])];
      const updatedProperties = [
        ...(updatedVariants[variantIndex].properties || []),
      ];

      updatedProperties[propIndex] = {
        ...updatedProperties[propIndex],
        [field]: value,
      };

      updatedVariants[variantIndex] = {
        ...updatedVariants[variantIndex],
        properties: updatedProperties,
      };

      return { ...prev, variants: updatedVariants };
    });
  };

  const addVariantProperty = (variantIndex: number) => {
    setFormData((prev) => {
      const updatedVariants = [...(prev.variants || [])];
      updatedVariants[variantIndex] = {
        ...updatedVariants[variantIndex],
        properties: [
          ...(updatedVariants[variantIndex].properties || []),
          { key: "", value: "" },
        ],
      };
      return { ...prev, variants: updatedVariants };
    });
  };

  const removeVariantProperty = (variantIndex: number, propIndex: number) => {
    setFormData((prev) => {
      const updatedVariants = [...(prev.variants || [])];
      const updatedProperties = [
        ...(updatedVariants[variantIndex].properties || []),
      ];
      updatedProperties.splice(propIndex, 1);

      updatedVariants[variantIndex] = {
        ...updatedVariants[variantIndex],
        properties: updatedProperties,
      };

      return { ...prev, variants: updatedVariants };
    });
  };

  const addVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [
        ...(prev.variants || []),
        {
          properties: [{ key: "", value: "" }],
          price: 0,
          stockQuantity: 0,
          sku: "",
        },
      ],
    }));
  };

  const removeVariant = (index: number) => {
    setFormData((prev) => {
      const updatedVariants = [...(prev.variants || [])];
      updatedVariants.splice(index, 1);
      return { ...prev, variants: updatedVariants };
    });
  };

  return (
    <div className="product_form">
      <h2>{isEditing ? "Edit Product" : "Add Product"}</h2>

      <form onSubmit={handleSubmit}>
        <div className="form-actions">
          <Buttons
            text={isEditing ? "Update" : "Add"}
            onClick={(e) => {
              e?.preventDefault();
              handleSubmit();
            }}
            variant={isEditing ? "update" : "add"}
          />
          <Buttons
            text="Cancel"
            onClick={() => {
              resetForm();
              setIsEditing(false);
            }}
            variant="cancel"
          />
        </div>
        <br/>
        {fieldTabs.flat().map((field) => (
          <div key={field.name} className="form-group">
            <label htmlFor={field.name}>{field.label}</label>

            {field.type === "select" && field.options ? (
              <select
                name={field.name}
                value={(formData as any)[field.name] ?? ""}
                onChange={handleChange}
              >
                <option value="">-- Select --</option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === "multiselect" && field.options ? (
              <select
                name={field.name}
                multiple
                value={
                  Array.isArray((formData as any)[field.name])
                    ? (formData as any)[field.name]
                    : []
                }
                onChange={(e) => {
                  const selected = Array.from(
                    e.target.selectedOptions,
                    (opt) => opt.value
                  );
                  setFormData((prev) => ({
                    ...prev,
                    [field.name]: selected,
                  }));
                }}
              >
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === "boolean" ? (
              <input
                type="checkbox"
                name={field.name}
                checked={(formData as any)[field.name] ?? false}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    [field.name]: e.target.checked,
                  }))
                }
              />
            ) :  field.type === "textarea" ? (                 // ← new branch
                <textarea
                  name={field.name}
                  id={field.name}
                  value={(formData as any)[field.name] ?? ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      [field.name]: e.target.value,
                    }))
                  }
                  rows={4}                                        // adjust as needed
                  className="your-textarea-class"                 // or inline styles
                />
              ):(
              <input
                type={field.type}
                name={field.name}
                value={(formData as any)[field.name] ?? ""}
                onChange={handleChange}
              />
            )}
          </div>
        ))}

        

        <div className="form-group">
          <label>Category</label>
          <select
            name="categoryId"
            value={formData.categoryId}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                categoryId: e.target.value,
              }))
            }
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Vendor</label>
          <select
            name="vendorId"
            value={formData.vendorId}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                vendorId: e.target.value,
                shopId: "",
              }))
            }
          >
            <option value="">Select Vendor</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.businessName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Shop</label>
          <select
            name="shopId"
            value={formData.shopId}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                shopId: e.target.value,
              }))
            }
          >
            <option value="">Select Shop</option>
            {filteredShops.length > 0 ? (
              filteredShops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.shopName}
                </option>
              ))
            ) : (
              <option disabled>No shops available</option>
            )}
          </select>
        </div>

        <div className="form-group">
          <label>Upload Image(s)</label>
          <input type="file" multiple onChange={handleImageChange} />
        </div>

        <div className="form-group">
          <label>Upload Video</label>
          <input type="file" accept="video/*" onChange={handleVideoChange} disabled/>
        </div>

        <br/>

        {/* Attributes Section */}
        <div className="form-group">
          <label>Attributes</label>
          {(formData.attributes || []).map((attr, index) => (
            <div
              key={index}
              className="attribute-row"
              style={{ display: "flex", gap: 8, marginBottom: 8 }}
            >
              <input
                type="text"
                placeholder="Key"
                value={attr.key}
                onChange={(e) =>
                  handleAttributeChange(index, "key", e.target.value)
                }
                style={{ flex: 1 }}
              />
              <input
                type="text"
                placeholder="Value"
                value={attr.value}
                onChange={(e) =>
                  handleAttributeChange(index, "value", e.target.value)
                }
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={() => removeAttribute(index)}
                className="remove-button"
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" onClick={addAttribute} className="add-button">
            + Add Attribute
          </button>
        </div>

        <br/>
        {/* Variants Section */}
        <div className="form-group">
          <label>Variants</label>
          {(formData.variants || []).map((variant, variantIndex) => (
            <div
              key={variantIndex}
              className="variant-card"
              style={{
                border: "1px solid #ddd",
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div
                className="variant-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <h4>Variant #{variantIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeVariant(variantIndex)}
                  className="remove-button"
                >
                  Remove Variant
                </button>
              </div>

              <div className="variant-properties" style={{ marginBottom: 16 }}>
                <label>Properties:</label>
                {(variant.properties || []).map((prop, propIndex) => (
                  <div
                    key={propIndex}
                    style={{ display: "flex", gap: 8, marginBottom: 8 }}
                  >
                    <input
                      type="text"
                      placeholder="Property Key"
                      value={prop.key}
                      onChange={(e) =>
                        handleVariantPropertyChange(
                          variantIndex,
                          propIndex,
                          "key",
                          e.target.value
                        )
                      }
                      style={{ flex: 1 }}
                    />
                    <input
                      type="text"
                      placeholder="Property Value"
                      value={prop.value}
                      onChange={(e) =>
                        handleVariantPropertyChange(
                          variantIndex,
                          propIndex,
                          "value",
                          e.target.value
                        )
                      }
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        removeVariantProperty(variantIndex, propIndex)
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addVariantProperty(variantIndex)}
                  style={{ marginTop: 8 }}
                >
                  + Add Property
                </button>
              </div>

              <div
                className="variant-details"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
              >
                <div>
                  <label>Price</label>
                  <input
                    type="number"
                    value={variant.price}
                    onChange={(e) =>
                      handleVariantChange(
                        variantIndex,
                        "price",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                </div>
                <div>
                  <label>Stock Quantity</label>
                  <input
                    type="number"
                    value={variant.stockQuantity}
                    onChange={(e) =>
                      handleVariantChange(
                        variantIndex,
                        "stockQuantity",
                        parseInt(e.target.value, 10)
                      )
                    }
                  />
                </div>
                <div>
                  <label>SKU</label>
                  <input
                    type="text"
                    value={variant.sku || ""}
                    onChange={(e) =>
                      handleVariantChange(variantIndex, "sku", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addVariant}
            className="add-button"
            style={{ marginTop: 16 }}
          >
            + Add Variant
          </button>
        </div>

        
      </form>

      <div
        className="image-previews"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          marginTop: "16px",
        }}
      >
        {imagePreviews.map((preview, index) => (
          <img
            key={index}
            src={preview}
            alt={`Preview ${index}`}
            style={{
              width: "200px",
              height: "200px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductForm;
